import {
  AuthenticationResultType,
  ChangePasswordCommand,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ConfirmSignUpCommand,
  DeleteUserCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  ResendConfirmationCodeCommand,
  SignUpCommand,
  UpdateUserAttributesCommand,
  VerifyUserAttributeCommand,
} from '@aws-sdk/client-cognito-identity-provider';

let cognitoClient: CognitoIdentityProviderClient;

const initClient = (region: string) => {
  if (!cognitoClient) {
    cognitoClient = new CognitoIdentityProviderClient({ region: region });
  }
};

export const cognitoSignUp = async (
  region: string,
  clientId: string,
  email: string,
  password: string,
) => {
  initClient(region);
  try {
    await cognitoClient.send(
      new SignUpCommand({
        ClientId: clientId,
        Username: email,
        Password: password,
        UserAttributes: [{ Name: 'email', Value: email }],
      }),
    );
    return true;
  } catch (err) {
    return false;
  }
};

export const cognitoConfirmSignUp = async (
  region: string,
  clientId: string,
  email: string,
  verificationCode: string,
) => {
  initClient(region);
  try {
    await cognitoClient.send(
      new ConfirmSignUpCommand({
        ClientId: clientId,
        Username: email,
        ConfirmationCode: verificationCode,
      }),
    );
    return true;
  } catch (err) {
    return false;
  }
};

export const cognitoResendConfirmationCode = async (
  region: string,
  clientId: string,
  email: string,
) => {
  initClient(region);
  try {
    await cognitoClient.send(
      new ResendConfirmationCodeCommand({
        ClientId: clientId,
        Username: email,
      }),
    );
    return true;
  } catch (err) {
    return false;
  }
};

// Define a discriminated union type for better type safety
type SignInResult =
  | { success: true; data: AuthenticationResultType }
  | { success: false; error: 'UserNotConfirmedException' | 'InvalidCredentials' | 'Unknown' };

export const cognitoSignIn = async (
  region: string,
  clientId: string,
  email: string,
  password: string,
): Promise<SignInResult> => {
  initClient(region);
  try {
    const response = await cognitoClient.send(
      new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      }),
    );

    if (response.AuthenticationResult) {
      return { success: true, data: response.AuthenticationResult };
    }
    return { success: false, error: 'Unknown' };
  } catch (err) {
    // Check if the error is due to unconfirmed user
    if (err instanceof Error && err.name === 'UserNotConfirmedException') {
      return { success: false, error: 'UserNotConfirmedException' };
    }
    return { success: false, error: 'InvalidCredentials' };
  }
};

// Define a discriminated union type for email change results
type EmailChangeResult =
  | { success: true }
  | { success: false; error: 'InvalidCredentials' | 'EmailAlreadyExists' | 'Unknown' };

export const cognitoUpdateEmail = async (
  region: string,
  accessToken: string,
  newEmail: string,
): Promise<EmailChangeResult> => {
  initClient(region);
  try {
    await cognitoClient.send(
      new UpdateUserAttributesCommand({
        AccessToken: accessToken,
        UserAttributes: [
          {
            Name: 'email',
            Value: newEmail,
          },
        ],
      }),
    );
    return { success: true };
  } catch (err) {
    if (err instanceof Error) {
      if (err.name === 'AliasExistsException' || err.name === 'InvalidParameterException') {
        return { success: false, error: 'EmailAlreadyExists' };
      }
    }
    return { success: false, error: 'Unknown' };
  }
};

export const cognitoVerifyEmail = async (
  region: string,
  accessToken: string,
  verificationCode: string,
): Promise<{ success: boolean; error?: string }> => {
  initClient(region);
  try {
    await cognitoClient.send(
      new VerifyUserAttributeCommand({
        AccessToken: accessToken,
        AttributeName: 'email',
        Code: verificationCode,
      }),
    );
    return { success: true };
  } catch (err) {
    return { success: false, error: 'Invalid verification code' };
  }
};

// Define a discriminated union type for delete user results
type DeleteUserResult =
  | { success: true }
  | { success: false; error: 'InvalidCredentials' | 'Unknown' };

export const cognitoDeleteUser = async (
  region: string,
  accessToken: string,
): Promise<DeleteUserResult> => {
  initClient(region);
  try {
    await cognitoClient.send(
      new DeleteUserCommand({
        AccessToken: accessToken,
      }),
    );
    return { success: true };
  } catch (err) {
    if (err instanceof Error) {
      if (err.name === 'NotAuthorizedException' || err.name === 'InvalidParameterException') {
        return { success: false, error: 'InvalidCredentials' };
      }
    }
    return { success: false, error: 'Unknown' };
  }
};

// Define a discriminated union type for forgot password results
type ForgotPasswordResult =
  | { success: true }
  | { success: false; error: 'UserNotFound' | 'LimitExceeded' | 'Unknown' };

export const cognitoForgotPassword = async (
  region: string,
  clientId: string,
  email: string,
): Promise<ForgotPasswordResult> => {
  initClient(region);
  try {
    await cognitoClient.send(
      new ForgotPasswordCommand({
        ClientId: clientId,
        Username: email,
      }),
    );
    return { success: true };
  } catch (err) {
    if (err instanceof Error) {
      if (err.name === 'UserNotFoundException') {
        return { success: false, error: 'UserNotFound' };
      }
      if (err.name === 'LimitExceededException') {
        return { success: false, error: 'LimitExceeded' };
      }
    }
    return { success: false, error: 'Unknown' };
  }
};

// Define a discriminated union type for confirm forgot password results
type ConfirmForgotPasswordResult =
  | { success: true }
  | { success: false; error: 'InvalidCode' | 'ExpiredCode' | 'InvalidPassword' | 'Unknown' };

export const cognitoConfirmForgotPassword = async (
  region: string,
  clientId: string,
  email: string,
  verificationCode: string,
  newPassword: string,
): Promise<ConfirmForgotPasswordResult> => {
  initClient(region);
  try {
    await cognitoClient.send(
      new ConfirmForgotPasswordCommand({
        ClientId: clientId,
        Username: email,
        ConfirmationCode: verificationCode,
        Password: newPassword,
      }),
    );
    return { success: true };
  } catch (err) {
    if (err instanceof Error) {
      if (err.name === 'CodeMismatchException') {
        return { success: false, error: 'InvalidCode' };
      }
      if (err.name === 'ExpiredCodeException') {
        return { success: false, error: 'ExpiredCode' };
      }
      if (err.name === 'InvalidPasswordException' || err.name === 'InvalidParameterException') {
        return { success: false, error: 'InvalidPassword' };
      }
    }
    return { success: false, error: 'Unknown' };
  }
};

// Define a discriminated union type for change password results
type ChangePasswordResult =
  | { success: true }
  | {
      success: false;
      error: 'WrongPassword' | 'InvalidPassword' | 'LimitExceeded' | 'NotAuthorized' | 'Unknown';
    };

export const cognitoChangePassword = async (
  region: string,
  accessToken: string,
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> => {
  initClient(region);
  try {
    await cognitoClient.send(
      new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: currentPassword,
        ProposedPassword: newPassword,
      }),
    );
    return { success: true };
  } catch (err) {
    if (err instanceof Error) {
      if (err.name === 'NotAuthorizedException') {
        return { success: false, error: 'WrongPassword' };
      }
      if (err.name === 'InvalidPasswordException' || err.name === 'InvalidParameterException') {
        return { success: false, error: 'InvalidPassword' };
      }
      if (err.name === 'LimitExceededException') {
        return { success: false, error: 'LimitExceeded' };
      }
    }
    return { success: false, error: 'Unknown' };
  }
};
